import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Pagination,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Business,
  Sync,
  Visibility
} from '@mui/icons-material';
import axios from 'axios';
import { getToken, base_url } from '../../config/config';

const StockOwnersManager = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    contactInfo: {
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      }
    }
  });
  const [alert, setAlert] = useState(null);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchOwners();
    fetchStats();
  }, [pagination.page, searchTerm]);

  const fetchOwners = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await axios.get(`${base_url}/v1/stock/owners`, { params, headers });
      setOwners(response.data.data);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.pagination?.totalPages || 1
      }));
    } catch (error) {
      console.error('Error fetching stock owners:', error);
      setAlert({ severity: 'error', message: 'Failed to fetch stock owners' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${base_url}/v1/stock/owners/stats`, { headers });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreate = () => {
    setEditingOwner(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      contactInfo: {
        email: '',
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          country: '',
          postalCode: ''
        }
      }
    });
    setOpenDialog(true);
  };

  const handleEdit = (owner) => {
    setEditingOwner(owner);
    setFormData({
      name: owner.name || '',
      code: owner.code || '',
      description: owner.description || '',
      contactInfo: {
        email: owner.contactInfo?.email || '',
        phone: owner.contactInfo?.phone || '',
        address: {
          street: owner.contactInfo?.address?.street || '',
          city: owner.contactInfo?.address?.city || '',
          state: owner.contactInfo?.address?.state || '',
          country: owner.contactInfo?.address?.country || '',
          postalCode: owner.contactInfo?.address?.postalCode || ''
        }
      }
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingOwner) {
        await axios.put(`${base_url}/v1/stock/owners/${editingOwner._id}`, formData, { headers });
        setAlert({ severity: 'success', message: 'Stock owner updated successfully' });
      } else {
        await axios.post(`${base_url}/v1/stock/owners`, formData, { headers });
        setAlert({ severity: 'success', message: 'Stock owner created successfully' });
      }
      
      setOpenDialog(false);
      await fetchOwners();
      await fetchStats();
    } catch (error) {
      console.error('Error saving stock owner:', error);
      setAlert({ 
        severity: 'error', 
        message: error.response?.data?.message || 'Failed to save stock owner' 
      });
    }
  };

  const handleDelete = async (ownerId) => {
    if (window.confirm('Are you sure you want to delete this stock owner?')) {
      try {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        await axios.delete(`${base_url}/v1/stock/owners/${ownerId}`, { headers });
        setAlert({ severity: 'success', message: 'Stock owner deleted successfully' });
        await fetchOwners();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting stock owner:', error);
        setAlert({ 
          severity: 'error', 
          message: error.response?.data?.message || 'Failed to delete stock owner' 
        });
      }
    }
  };

  const handleSyncWithOdoo = async () => {
    try {
      setSyncing(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${base_url}/v1/stock/owners/odoo/sync`, {}, { headers });
      const results = response.data.data;
      
      setAlert({ 
        severity: 'success', 
        message: `Sync completed: ${results.success.length} successful, ${results.failed.length} failed` 
      });
    } catch (error) {
      console.error('Error syncing with Odoo:', error);
      setAlert({ 
        severity: 'error', 
        message: error.response?.data?.message || 'Failed to sync with Odoo' 
      });
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child, grandchild] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: grandchild ? {
            ...prev[parent][child],
            [grandchild]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Stock Owners Management
      </Typography>

      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {stats.totalOwners}
                </Typography>
                <Typography variant="body2">Total Owners</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {stats.activeOwners}
                </Typography>
                <Typography variant="body2">Active Owners</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {stats.inactiveOwners}
                </Typography>
                <Typography variant="body2">Inactive Owners</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="secondary">
                  {stats.topOwnersByProducts?.length || 0}
                </Typography>
                <Typography variant="body2">Top Owners</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <TextField
          placeholder="Search stock owners..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />

        <Box>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
            onClick={handleSyncWithOdoo}
            disabled={syncing}
            sx={{ mr: 1 }}
          >
            {syncing ? 'Syncing...' : 'Sync with Odoo'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Create Stock Owner
          </Button>
        </Box>
      </Box>

      {/* Owners Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : owners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No stock owners found
                  </TableCell>
                </TableRow>
              ) : (
                owners.map((owner) => (
                  <TableRow key={owner._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Business sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="subtitle2">
                            {owner.name}
                          </Typography>
                          {owner.description && (
                            <Typography variant="body2" color="text.secondary">
                              {owner.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={owner.code} size="small" />
                    </TableCell>
                    <TableCell>
                      {owner.contactInfo?.email && (
                        <Typography variant="body2">
                          {owner.contactInfo.email}
                        </Typography>
                      )}
                      {owner.contactInfo?.phone && (
                        <Typography variant="body2">
                          {owner.contactInfo.phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={owner.isActive ? 'Active' : 'Inactive'}
                        color={owner.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {owner.stats?.productCount || 0}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(owner)} size="small">
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(owner._id)} 
                        size="small"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOwner ? 'Edit Stock Owner' : 'Create New Stock Owner'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value)}
                helperText="Leave empty to auto-generate"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>
            
            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.contactInfo.email}
                onChange={(e) => handleFormChange('contactInfo.email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.contactInfo.phone}
                onChange={(e) => handleFormChange('contactInfo.phone', e.target.value)}
              />
            </Grid>
            
            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street"
                value={formData.contactInfo.address.street}
                onChange={(e) => handleFormChange('contactInfo.address.street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.contactInfo.address.city}
                onChange={(e) => handleFormChange('contactInfo.address.city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.contactInfo.address.state}
                onChange={(e) => handleFormChange('contactInfo.address.state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.contactInfo.address.country}
                onChange={(e) => handleFormChange('contactInfo.address.country', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.contactInfo.address.postalCode}
                onChange={(e) => handleFormChange('contactInfo.address.postalCode', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingOwner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockOwnersManager;